import * as React from "react";
import { canUseDOM } from "../utils.js";
import {
  CreateOrganizationApiKeyResponse,
  ListMetadata,
} from "../../api/endpoint.js";

export function useApiKeysState(initialState: ApiKeysState) {
  const [[state, effects], dispatch] = React.useReducer(reducer, [
    initialState,
    [],
  ]);

  React.useEffect(() => {
    if (window !== window.top) {
      // do not use query params if widget is rendered in an iframe
      return;
    }
    const params = new URLSearchParams(window.location.search);
    dispatch({ type: "INIT", params });
  }, []);

  React.useEffect(() => {
    for (const effect of effects) {
      effect();
    }
  }, [effects]);

  return [state, dispatch] as const;
}

function reducer(
  current: StateWithEffects<ApiKeysState>,
  action: ApiKeysAction,
): StateWithEffects<ApiKeysState> {
  const effects: EffectFn[] = [];
  const exec = (fn: () => void) => {
    const effect = () => {
      if (!effect.disposed) {
        effect.disposed = true;
        fn();
      }
    };
    effect.disposed = false;
    effects.push(effect);
  };

  const [currentState] = current;
  switch (action.type) {
    case "INIT": {
      const { params } = action;
      let searchQuery;
      if ((searchQuery = params.get("ak_q"))) {
        return [{ ...currentState, searchQuery }, effects];
      }
      return current;
    }
    case "SET_PAGINATION": {
      const { pagination } = action;
      if (
        (pagination.after &&
          pagination.after === currentState.pagination?.after) ||
        (pagination.before &&
          pagination.before === currentState.pagination?.before)
      ) {
        return current;
      }

      return [{ ...currentState, pagination }, effects];
    }
    case "FILTER_BY_SEARCH": {
      if (action.searchQuery === currentState.searchQuery) {
        return current;
      }
      const { searchQuery } = action;
      exec(() =>
        updateQueryParams({ ak_q: searchQuery }, { replaceState: true }),
      );
      return [{ ...currentState, pagination: null, searchQuery }, effects];
    }
    case "OPEN_CREATE_DIALOG": {
      return [
        {
          ...currentState,
          createDialogOpen: true,
          createdApiKey: null,
        },
        effects,
      ];
    }
    case "CLOSE_CREATE_DIALOG": {
      return [
        {
          ...currentState,
          createDialogOpen: false,
          createdApiKey: null,
        },
        effects,
      ];
    }
    case "SET_CREATED_API_KEY": {
      return [
        {
          ...currentState,
          createdApiKey: action.apiKey,
        },
        effects,
      ];
    }
    default:
      return current;
  }
}

export interface ApiKeysState {
  searchQuery: string | null;
  pagination: ListMetadata | null;
  createDialogOpen: boolean;
  createdApiKey: CreateOrganizationApiKeyResponse | null;
}

export type ApiKeysAction =
  | { type: "INIT"; params: URLSearchParams }
  | { type: "FILTER_BY_SEARCH"; searchQuery: string | null }
  | { type: "SET_PAGINATION"; pagination: ListMetadata }
  | { type: "OPEN_CREATE_DIALOG" }
  | { type: "CLOSE_CREATE_DIALOG" }
  | { type: "SET_CREATED_API_KEY"; apiKey: CreateOrganizationApiKeyResponse };

type EffectFn = {
  (): void;
  disposed: boolean;
};

type StateWithEffects<State> = [State, EffectFn[]];

function updateQueryParams(
  newParams: { [key: string]: { toString(): string } | null },
  args: { replaceState?: boolean } = {},
) {
  if (!canUseDOM) {
    throw new Error("Cannot update query params on the server");
  }

  if (window !== window.top) {
    // do not update query params if widget is rendered in an iframe
    return;
  }

  const initialSearch = window.location.search;
  const params = new URLSearchParams(initialSearch);
  Object.entries(newParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      params.set(key, String(value));
    } else {
      params.delete(key);
    }
  });

  if (initialSearch === `?${params.toString()}`) {
    return;
  }

  const newUrl = params.toString()
    ? `${window.location.pathname}?${params}`
    : window.location.pathname;

  if (args.replaceState) {
    window.history.replaceState({}, "", newUrl);
  } else {
    window.history.pushState({}, "", newUrl);
  }
}
